'use client';

import type { Tag as ITag } from '@baseball-near-you/prisma';
import Fuse from 'fuse.js';
import { useMemo, useState } from 'react';
import { LargeCheckboxInput } from '../components';
import { Input, Separator } from '../ui';
import { Tag } from './Tag';

export function TagSelect({
  tags,
  tagsLoading,
  ...props
}: {
  tags?: ITag[];
  tagsLoading: boolean;
  selectedTags: number[];
  onTagSelect: (tag: ITag) => void;
}) {
  const [search, setSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState<ITag[]>(tags ?? []);

  const fuse = useMemo(() => {
    if (tags) {
      setFilteredTags(tags);
      return new Fuse(tags, { keys: ['name'] });
    }
  }, [tags]);

  return (
    <div className="flex flex-col">
      <Input
        onChange={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setSearch(e.target.value);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        placeholder="Search for a tag..."
        className="placeholder:font-medium"
      />
      <div className="relative mt-2 h-[13.5rem] gap-2 overflow-auto">
        {(search && fuse ? fuse.search(search) : filteredTags).map((tag) => {
          let result = tag as ITag;
          if (search && fuse) {
            result = (tag as Fuse.FuseResult<ITag>).item;
          }

          const checked = props.selectedTags.includes(result.id);

          return (
            <LargeCheckboxInput
              label={result.name}
              checked={checked}
              key={`menu-tag-${result.name}-${result.id}`}
              onChange={(e) => {
                props.onTagSelect(result);
              }}
              className={'mr-1 mb-1 inline-flex w-min whitespace-nowrap p-1.5'}
            />
          );
        })}
      </div>
      <Separator className="mb-2" />
      <div className="mt-5">
        <p className="mb-2 font-medium text-gray-700 text-sm">Selected tags:</p>
        <div className="h-40 overflow-y-scroll rounded-md border-[1px] border-gray-200 bg-gray-50/50 px-4 py-2">
          {!!props.selectedTags?.length && !!tags ? (
            <div className="flex flex-wrap gap-1.5">
              {props.selectedTags.map((tagId) => {
                const tag = tags.find((tag) => tag.id === tagId);
                return tag ? (
                  <Tag key={`selected-tag-${tagId}`} {...tag} />
                ) : null;
              })}
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray-400">
              no tags selected
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
